import { ADDON_CONTENTS, isOpen, type Homework } from '@adhd/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '../components/Icons.js';
import { formatUntil } from '../components/KeepInMind.js';
import { api } from '../lib/api.js';

/**
 * Homework: everything you have been set, and the way to start another.
 *
 * REACHED FROM THE LOG, beside Print or save and Share with someone. Homework
 * is set by somebody else and taken back to them, which makes it a thing you do
 * about the record rather than one of the ways to log a day — and it keeps
 * Today clean, which is the point. Answering one is the only part that reaches
 * Today, and only while one is running.
 */
export function HomeworkIndex(): JSX.Element {
  const [items, setItems] = useState<Homework[] | null>(null);
  const [today, setToday] = useState('');

  useEffect(() => {
    api
      .homework()
      .then((r) => {
        setItems(r.items);
        setToday(r.today);
      })
      .catch(() => setItems([]));
  }, []);

  const running = (items ?? []).filter((h) => today && isOpen(h, today));
  const finished = (items ?? []).filter((h) => !(today && isOpen(h, today)));

  return (
    <div className="screen stack">
      <Link className="linkish back-link" to="/days">
        <ArrowLeftIcon /> Log
      </Link>

      <header className="stack-sm stack">
        <h1>Therapy homework</h1>
        <p className="muted">
          A question you want put in front of you for a while — set by a therapist, or your own. It
          appears on Today until the day you set, and then it stops.{' '}
          {/*
            TRUE AS OF 1 OCTOBER 2026, and it was not before.

            This read "Nothing you share can see any of it: not what you write,
            not that it exists, not how often you answer." That was exactly
            right on the day it was written — homework had no grant at any
            level. Then the Therapy homework add-on was built, at Nick's
            asking, so his therapist could be given the thing she had set him
            — and this paragraph was never revisited. It went on telling him
            his homework could never be shared while a link that shares it
            already existed and Katie held one.

            A screen that overstates privacy is worse than one that says
            nothing, and this is the screen whose whole subject is who may
            read a thing.

            The second half still holds and is worth keeping: a link WITHOUT
            the add-on cannot tell that any of this exists. Not the words, not
            the cadence, not that a question is running. The smoke suite pins
            the shared payload byte-identical with and without homework
            present, which is what makes that a fact rather than a hope.
          */}
          {/* The add-on's name comes from ADDON_CONTENTS, not typed here: this
              sentence is the only place in the app that tells somebody which
              tick governs their homework, and a renamed add-on would leave it
              naming one that no longer exists. */}
          <strong>
            Only a link carrying the &ldquo;{ADDON_CONTENTS.homework.name}&rdquo; add-on can see
            any of this
          </strong>
          : the words you write and the days you wrote them. Every other link cannot tell it
          exists at all — not the writing, not how often, not that a question is running.
        </p>
      </header>

      {running.map((h) => (
        <Link key={h.id} className="option" to={`/homework/${h.id}`}>
          <strong>{h.title}</strong>
          <span>
            Until {formatUntil(h.endsOn)}
            {h.answeredDays > 0 &&
              ` · written on ${h.answeredDays} ${h.answeredDays === 1 ? 'day' : 'days'}`}
          </span>
        </Link>
      ))}

      {/* One at a time, so this is an explanation rather than a disabled
          button — a control that cannot be pressed teaches nothing. */}
      {running.length > 0 ? (
        <p className="faint">
          One runs at a time. Stop this one, or let it finish, and you can start another.
        </p>
      ) : (
        <Link className="btn btn-primary btn-block" to="/homework/new">
          Start one
        </Link>
      )}

      {finished.length > 0 && (
        <section className="stack-sm stack">
          <h2>Finished</h2>
          {finished.map((h) => (
            <Link key={h.id} className="option" to={`/homework/${h.id}`}>
              <strong>{h.title}</strong>
              <span>
                Ended {formatUntil(h.endsOn)}
                {h.answeredDays > 0 &&
                  ` · written on ${h.answeredDays} ${h.answeredDays === 1 ? 'day' : 'days'}`}
              </span>
            </Link>
          ))}
        </section>
      )}

      {items !== null && items.length === 0 && (
        <p className="muted">Nothing yet. Start one when someone asks you to keep track of something.</p>
      )}
    </div>
  );
}
